import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import Button from '../../../ui/Button/Button'
import StatusMessage, { useStatusMessage } from '../../../ui/StatusMessage/StatusMessage'
import Spinner from '../../../ui/Spinner/Spinner'
import DataTable from '../../../ui/DataTable/DataTable'
import {
  createBlankGiftFundItem,
  cloneGiftFundItem,
  getFundCatalogSourceLabel,
  getFundItemAvatarImageUrl,
  getFundItemBrandLabel,
  getFundItemDisplayTitle,
  getFundItemProductCode,
  getFundItemQuantityLabel,
  getFundItemRemainValue,
  getFundItemVolumeLabel,
  getFundTypeLabel,
  GIFT_FUND_UNKNOWN_TIER_ID,
  groupGiftFundItemsByAvailableFromTier,
} from '../../../api/marketing/gifts/giftFundHelpers'
import { getGiftFundCatalogPoolTablePresentation, formatGiftFundOfferCountLabel } from '../../../api/marketing/gifts/giftFundCatalogPool'
import {
  getSortedTiers,
  getTierFromAndAboveLabel,
} from '../../../api/marketing/gifts/giftWithPurchaseConfigHelpers'
import { useGiftsConfig } from './GiftsConfigContext'
import { useGiftsFund } from './GiftsFundContext'
import GiftsFundItemEditor from './GiftsFundItemEditor'
import GiftsFundTableFilters, { buildGiftFundGroupFilterOptions } from './GiftsFundTableFilters'
import {
  filterGiftFundItems,
  GIFT_FUND_GROUP_FILTER_ALL,
  GIFT_FUND_STATUS_FILTER,
  GIFT_FUND_TYPE_FILTER,
} from './filterGiftFundItems'
import {
  getGiftsFundTableColumns,
} from './getGiftsFundTableColumns'
import styles from './GiftsFund.module.scss'

const enrichFundTableRow = (item) => ({
  ...item,
  displayCode: getFundItemProductCode(item),
  displayBrand: getFundItemBrandLabel(item) || '—',
  displayTitle: getFundItemDisplayTitle(item),
  displayVolume: getFundItemVolumeLabel(item) || '—',
  displayType: getFundTypeLabel(item),
  displayTypeSecondary: getFundCatalogSourceLabel(item),
  imageUrl: getFundItemAvatarImageUrl(item),
  remainValue: getFundItemRemainValue(item),
  remainLabel: getFundItemQuantityLabel(item),
})

function GiftsFundItemsTable({ tableKey, rows, columns, emptyMessage, onEdit }) {
  if (rows.length === 0) {
    return <p className={styles.empty}>{emptyMessage}</p>
  }

  return (
    <div className={styles.fundTable}>
      <DataTable
        key={tableKey}
        initialState={{
          sorting: {
            sortModel: [{ field: 'displayTitle', sort: 'asc' }],
          },
        }}
        table={{
          rows,
          columns,
          autoHeight: true,
          disableRowSelectionOnClick: true,
          onRowDoubleClick: (params) => onEdit(params.row),
        }}
      />
    </div>
  )
}

function GiftsFund() {
  const { draft, sortedGroups } = useGiftsConfig()
  const {
    activeItems,
    archivedItems,
    loadStatus,
    loadError,
    isSaving,
    reloadItems,
    saveItem,
    archiveItem,
    restoreItem,
    catalogAvailability,
  } = useGiftsFund()
  const [editorDraft, setEditorDraft] = useState(null)
  const [isNew, setIsNew] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [typeFilter, setTypeFilter] = useState(GIFT_FUND_TYPE_FILTER.all)
  const [groupFilter, setGroupFilter] = useState(GIFT_FUND_GROUP_FILTER_ALL)
  const [statusFilter, setStatusFilter] = useState(GIFT_FUND_STATUS_FILTER.all)
  const [searchQuery, setSearchQuery] = useState('')
  const [tablePageKey, setTablePageKey] = useState(0)

  const groupsById = useMemo(
    () => Object.fromEntries(sortedGroups.map((group) => [group.id, group])),
    [sortedGroups]
  )

  const sortedTiers = useMemo(() => getSortedTiers(draft?.tiers || []), [draft?.tiers])

  const tiersById = useMemo(
    () => Object.fromEntries(sortedTiers.map((tier) => [tier.id, tier])),
    [sortedTiers]
  )

  const groupFilterOptions = useMemo(() => buildGiftFundGroupFilterOptions(sortedGroups), [sortedGroups])

  const filterState = useMemo(
    () => ({ typeFilter, groupFilter, statusFilter, searchQuery }),
    [typeFilter, groupFilter, statusFilter, searchQuery]
  )

  useEffect(() => {
    setTablePageKey((value) => value + 1)
  }, [typeFilter, groupFilter, statusFilter, searchQuery])

  const filteredActiveItems = useMemo(
    () => filterGiftFundItems(activeItems, filterState),
    [activeItems, filterState]
  )

  const filteredArchivedItems = useMemo(
    () => filterGiftFundItems(archivedItems, filterState),
    [archivedItems, filterState]
  )

  const toTableRows = useCallback(
    (items) =>
      items.map((item) => {
        const tier = tiersById[item.availableFromTierId]
        const presentation = getGiftFundCatalogPoolTablePresentation(item, catalogAvailability)
        return {
          ...enrichFundTableRow({
            ...item,
            groupName: groupsById[item.rewardGroupId]?.name || '—',
            tierLabel: tier ? getTierFromAndAboveLabel(tier) : '—',
            tierMinAmount: tier && Number.isFinite(Number(tier.minAmount)) ? Number(tier.minAmount) : null,
          }),
          remainLabel: presentation.remainLabel,
          remainValue: presentation.remainValue,
          displayTitleSecondary: presentation.secondary,
          useSetAvatar: presentation.useSetAvatar,
        }
      }),
    [groupsById, tiersById, catalogAvailability]
  )

  const activeGroups = useMemo(
    () =>
      groupGiftFundItemsByAvailableFromTier(filteredActiveItems, sortedTiers).map((group) => ({
        ...group,
        rows: toTableRows(group.items),
      })),
    [filteredActiveItems, sortedTiers, toTableRows]
  )

  const archivedGroups = useMemo(
    () =>
      groupGiftFundItemsByAvailableFromTier(filteredArchivedItems, sortedTiers)
        .map((group) => ({
          ...group,
          rows: toTableRows(group.items),
        }))
        .filter((group) => group.rows.length > 0),
    [filteredArchivedItems, sortedTiers, toTableRows]
  )

  const defaultGroupId = sortedGroups[0]?.id || null
  const defaultTierId = sortedTiers[0]?.id || null

  const openCreate = (tierId = defaultTierId) => {
    setIsNew(true)
    setEditorDraft(
      createBlankGiftFundItem({
        rewardGroupId: defaultGroupId,
        availableFromTierId: tierId || defaultTierId,
      })
    )
  }

  const openEdit = (item) => {
    setIsNew(false)
    setEditorDraft(cloneGiftFundItem(item))
  }

  const closeEditor = () => {
    setEditorDraft(null)
    setIsNew(false)
  }

  const handleSave = async (item) => {
    const nextItem = item.isArchived ? { ...item, isActive: false } : item
    const result = await saveItem(nextItem)

    if (!result.ok) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: result.message || 'Не вдалося зберегти позицію',
        closeAfter: 5000,
      })
      return { ok: false, message: result.message }
    }

    setStatusMessage({
      show: true,
      type: 'success',
      message: isNew ? 'Позицію збережено' : 'Позицію оновлено',
      closeAfter: 3000,
    })
    closeEditor()
    return { ok: true }
  }

  const handleArchive = async (item) => {
    const confirmed = window.confirm(`Архівувати «${getFundItemDisplayTitle(item)}»?`)
    if (!confirmed) {
      return
    }

    const result = await archiveItem(item.id)
    if (!result.ok) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: result.message || 'Не вдалося архівувати',
        closeAfter: 5000,
      })
      return
    }

    setStatusMessage({
      show: true,
      type: 'success',
      message: 'Позицію архівовано',
      closeAfter: 3000,
    })
  }

  const handleRestore = async (item) => {
    const result = await restoreItem(item.id)
    if (!result.ok) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: result.message || 'Не вдалося відновити',
        closeAfter: 5000,
      })
      return
    }

    setStatusMessage({
      show: true,
      type: 'success',
      message: 'Відновлено як неактивну',
      closeAfter: 3000,
    })
  }

  const activeColumns = useMemo(
    () =>
      getGiftsFundTableColumns({
        onEdit: openEdit,
        getRowActions: (row) => [
          {
            id: 'edit',
            icon: 'pencil-alt',
            label: 'Змінити',
            onClick: () => openEdit(row),
          },
          {
            id: 'archive',
            icon: 'archive',
            label: 'Архів',
            onClick: () => handleArchive(row),
          },
        ],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSaving]
  )

  const archivedColumns = useMemo(
    () =>
      getGiftsFundTableColumns({
        onEdit: openEdit,
        getRowActions: (row) => [
          {
            id: 'edit',
            icon: 'pencil-alt',
            label: 'Змінити',
            onClick: () => openEdit(row),
          },
          {
            id: 'restore',
            icon: 'undo',
            label: 'Відновити',
            onClick: () => handleRestore(row),
          },
        ],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSaving]
  )

  if (editorDraft) {
    return (
      <div className={styles.root}>
        {statusMessage.show ? (
          <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
        ) : null}
        <GiftsFundItemEditor
          draft={editorDraft}
          isNew={isNew}
          isSaving={isSaving}
          onChange={setEditorDraft}
          onCancel={closeEditor}
          onSave={handleSave}
        />
      </div>
    )
  }

  if (loadStatus === 'loading') {
    return (
      <div className={styles.root}>
        <Spinner type="module" />
      </div>
    )
  }

  if (loadStatus === 'error') {
    return (
      <div className={styles.root}>
        <ModulePanel title="Подарунковий фонд">
          <p className={styles.error}>{loadError || 'Не вдалося завантажити фонд'}</p>
          <Button title="Повторити завантаження" theme="solid" fill="accent" size={32} onClick={reloadItems} />
        </ModulePanel>
      </div>
    )
  }

  const hasActiveSource = activeItems.length > 0
  const hasArchivedSource = archivedItems.length > 0
  const activeEmptyMessage = hasActiveSource ? 'Нічого не знайдено' : 'Немає позицій'
  const archivedEmptyMessage = hasArchivedSource ? 'Нічого не знайдено' : 'Немає позицій'

  return (
    <div className={styles.root}>
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}

      <div className={styles.tableStack}>
        <ModulePanel
          title={`Подарунковий фонд (${filteredActiveItems.length})`}
          buttons={
            <Button theme="circle" fill="accent" icon="plus" tooltip="Додати позицію" onClick={() => openCreate()} />
          }
        >
          <GiftsFundTableFilters
            typeFilter={typeFilter}
            groupFilter={groupFilter}
            statusFilter={statusFilter}
            searchQuery={searchQuery}
            groupOptions={groupFilterOptions}
            onChange={(next) => {
              if (next.typeFilter != null) setTypeFilter(next.typeFilter)
              if (next.groupFilter != null) setGroupFilter(next.groupFilter)
              if (next.statusFilter != null) setStatusFilter(next.statusFilter)
              if (next.searchQuery != null) setSearchQuery(next.searchQuery)
            }}
          />
        </ModulePanel>

        {activeGroups.map((group) => (
          <ModulePanel
            key={`active-${group.id}`}
            title={`${group.title} · ${formatGiftFundOfferCountLabel(group.rows.length)}`}
            buttons={
              group.id !== GIFT_FUND_UNKNOWN_TIER_ID ? (
                <Button
                  theme="circle"
                  fill="accent"
                  icon="plus"
                  tooltip="Додати позицію"
                  onClick={() => openCreate(group.id)}
                />
              ) : null
            }
          >
            <GiftsFundItemsTable
              tableKey={`active-${group.id}-${tablePageKey}`}
              rows={group.rows}
              columns={activeColumns}
              emptyMessage={activeEmptyMessage}
              onEdit={openEdit}
            />
          </ModulePanel>
        ))}

        {archivedGroups.length === 0 ? (
          <ModulePanel title={`Архів (${filteredArchivedItems.length})`}>
            <p className={styles.empty}>{archivedEmptyMessage}</p>
          </ModulePanel>
        ) : (
          archivedGroups.map((group) => (
            <ModulePanel
              key={`archived-${group.id}`}
              title={`Архів · ${group.title} · ${formatGiftFundOfferCountLabel(group.rows.length)}`}
            >
              <GiftsFundItemsTable
                tableKey={`archived-${group.id}-${tablePageKey}`}
                rows={group.rows}
                columns={archivedColumns}
                emptyMessage={archivedEmptyMessage}
                onEdit={openEdit}
              />
            </ModulePanel>
          ))
        )}
      </div>
    </div>
  )
}

export default GiftsFund
